import React, { useState } from 'react';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaWhatsapp, FaUsers } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import toast from 'react-hot-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt className="text-green-600 text-xl" />, title: 'Visit Us',
      details: 'Village & Post Addra, District Barabanki, Uttar Pradesh - 225207, India'
    },
    {
      icon: <FaPhoneAlt className="text-green-600 text-xl" />, title: 'Call Us',
      details: '+91 84230 45733', action: 'tel:+918423045733'
    },
    {
      icon: <FaWhatsapp className="text-green-600 text-xl" />, title: 'WhatsApp',
      details: '+91 84230 45733', action: 'https://wa.me/918423045733'
    },
    {
      icon: <FaEnvelope className="text-green-600 text-xl" />, title: 'Email Us',
      details: 'Assetsdairyfarm@gmail.com', action: 'mailto:Assetsdairyfarm@gmail.com'
    },
    {
      icon: <FaClock className="text-green-600 text-xl" />, title: 'Working Hours',
      details: 'Monday - Saturday: 9:00 AM - 7:00 PM'
    },
    {
      icon: <FaUsers className="text-green-600 text-xl" />, title: 'Business Partners',
      details: 'Md. & Co. Utkarsh Verma'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - Assets Dairy Farm</title>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <div className="bg-gradient-to-r from-green-700 to-green-900 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
            <p className="text-xl max-w-2xl mx-auto opacity-90">Assets Dairy Farm - Your Trusted Dairy Investment Partner</p>
          </div>
        </div>

        {/* Company Info Banner */}
        <div className="bg-green-50 py-3 border-b border-green-200">
          <div className="container mx-auto px-4 text-center">
            <p className="text-green-800 font-medium">
              🐄 Assets Dairy Farm | Village & Post Addra, District Barabanki, Uttar Pradesh - 225207
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 rounded-xl p-6 sticky top-24">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Get in Touch</h2>
                <div className="space-y-6">
                  {contactInfo.map((info, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{info.title}</h3>
                        {info.action ? (
                          <a href={info.action} target={info.title === 'WhatsApp' ? '_blank' : '_self'} rel="noopener noreferrer" className="text-gray-600 hover:text-green-600 transition">
                            {info.details}
                          </a>
                        ) : (
                          <p className="text-gray-600">{info.details}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t">
                  <h3 className="font-semibold text-gray-800 mb-3">Follow Us</h3>
                  <div className="flex gap-3">
                    <a href="#" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-green-600 hover:text-white transition"><FaFacebook /></a>
                    <a href="#" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-green-600 hover:text-white transition"><FaTwitter /></a>
                    <a href="#" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-green-600 hover:text-white transition"><FaInstagram /></a>
                    <a href="#" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-green-600 hover:text-white transition"><FaLinkedin /></a>
                    <a href="https://wa.me/918423045733" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-green-600 hover:text-white transition"><FaWhatsapp /></a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                      <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="John Doe" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="john@example.com" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                    <input type="text" name="subject" required value={formData.subject} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Investment Inquiry" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                    <textarea name="message" rows="5" required value={formData.message} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" placeholder="Tell us about your query..."></textarea>
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50">
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Map - Barabanki Location */}
        <div className="container mx-auto px-4 pb-16">
          <div className="bg-gray-200 rounded-xl overflow-hidden h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28475.000000000004!2d81.19!3d26.92!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3999579b3f4d6b6b%3A0x3f5b2f2b7c5d0b5d!2sBarabanki%2C%20Uttar%20Pradesh!5e0!3m2!1sen!2sin!4v1700000000000"
              width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              title="Assets Dairy Farm Location">
            </iframe>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;